import {skipToken} from '@reduxjs/toolkit/query';

import {find, isNil, isNotNil} from 'ramda';

import {useGetCommentsQuery, useGetTireSetCountQuery} from '@dms/api/core';
import {useGetCustomerV2Query, useGetVehicleCustomerQuery} from '@dms/api/customer';
import {useGetAdditionalNoteQuery, useGetVehicleQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {useGetVehicleLocalActionNoteQuery} from '@dms/api/vehicle';
import {
  useGetCaseItemsTotalAmountQuery,
  useGetMetadaServiceCaseQuery,
  useGetPendingServiceItemsQuery,
  useGetPendingServiceOrderReturnQuery,
  useGetPostponeJobsQuery,
  useGetServiceCaseActionsQuery,
  useGetServiceCaseAdditionalInformationQuery,
  useGetServiceCaseOrderCountQuery,
  useGetServiceCaseOrderVariantsQuery,
  useGetServiceCheckInQuery,
  useGetVehicleServiceCampaignQuery,
} from '@dms/api/workshop';
import {getCustomerName} from '@dms/shared';

import {useServiceCaseDetailBootstrap} from './useServiceCaseDetailBootstrap';

interface UseServiceCaseDetailDataProps {
  serviceCaseId: string;
}

// eslint-disable-next-line complexity -- Pre-existing violation
export function useServiceCaseDetailData(props: UseServiceCaseDetailDataProps) {
  const {serviceCaseId} = props;

  const serviceCaseQuery = useGetMetadaServiceCaseQuery({serviceCaseId});
  const {bootstrap, isBootstrapSettled, isBootstrapLoading} =
    useServiceCaseDetailBootstrap(serviceCaseId);

  const vehicleId = serviceCaseQuery.currentData?.vehicleId ?? bootstrap?.case?.vehicleId;
  const vehicleQuery = useGetVehicleQuery(isNotNil(vehicleId) ? {vehicleId} : skipToken);

  const commentsQuery = useGetCommentsQuery(
    isNotNil(vehicleId) ? {resourceId: EntityResourceIds.vehicle, recordId: vehicleId} : skipToken
  );

  const additionalNoteQuery = useGetAdditionalNoteQuery(
    isNotNil(vehicleId) ? {vehicleId} : skipToken
  );

  const vehicleCustomersFallbackQuery = useGetVehicleCustomerQuery(
    isBootstrapSettled &&
      isNotNil(vehicleId) &&
      (isNil(bootstrap?.vehicle?.vehicleCustomersCount) || isNil(bootstrap?.operatorCustomer))
      ? {vehicleId}
      : skipToken
  );

  const vehicleCustomersCount =
    bootstrap?.vehicle?.vehicleCustomersCount ?? vehicleCustomersFallbackQuery.data?.length;

  const vehicleOperator = find(
    (vehicleCustomer) =>
      isNotNil(vehicleCustomer) &&
      vehicleCustomer.customerType === 'CORVEHICLECUSTOMERTYPE_OPERATOR',
    vehicleCustomersFallbackQuery.data ?? []
  );
  const operatorCustomerId = vehicleOperator?.customerId;
  const operatorCustomerFallbackQuery = useGetCustomerV2Query(
    isBootstrapSettled && isNil(bootstrap?.operatorCustomer) && isNotNil(operatorCustomerId)
      ? {customerId: operatorCustomerId}
      : skipToken
  );

  const seededQueryArg = isBootstrapSettled ? {serviceCaseId} : skipToken;
  const postponedJobsQuery = useGetPostponeJobsQuery(seededQueryArg);
  const serviceCaseActionsQuery = useGetServiceCaseActionsQuery(seededQueryArg);
  const itemsTotalAmountQuery = useGetCaseItemsTotalAmountQuery(seededQueryArg);
  const serviceCaseOrderCountQuery = useGetServiceCaseOrderCountQuery(seededQueryArg);
  const pendingServiceItemsQuery = useGetPendingServiceItemsQuery(seededQueryArg);
  const pendingServiceOrderReturnQuery = useGetPendingServiceOrderReturnQuery(seededQueryArg);
  const orderVariantsQuery = useGetServiceCaseOrderVariantsQuery(seededQueryArg);

  // A missing bootstrap (aggregate error/204) leaves no snapshot at all — these granular
  // fallbacks restore the pre-bootstrap behavior for the remaining direct-read domains.
  // They cost nothing on a healthy load and stay off during the settle window.
  const isBootstrapMissing = isBootstrapSettled && isNil(bootstrap);
  const checkInFallbackQuery = useGetServiceCheckInQuery(
    isBootstrapMissing ? {serviceCaseId} : skipToken
  );
  const additionalInformationFallbackQuery = useGetServiceCaseAdditionalInformationQuery(
    isBootstrapMissing ? {serviceCaseId} : skipToken
  );
  const vehicleServiceCampaignFallbackQuery = useGetVehicleServiceCampaignQuery(
    isBootstrapMissing && isNotNil(vehicleId)
      ? {vehicleId, branchId: serviceCaseQuery.currentData?.branchId}
      : skipToken
  );
  const vehicleLocalActionNoteFallbackQuery = useGetVehicleLocalActionNoteQuery(
    isBootstrapMissing && isNotNil(vehicleId) ? {vehicleId} : skipToken
  );
  const tireSetCountFallbackQuery = useGetTireSetCountQuery(
    isBootstrapMissing && isNotNil(vehicleId) ? {vehicleId, states: ['INSTOCK']} : skipToken
  );

  // currentData empties on an arg change while isLoading stays false, so navigating service case
  // to service case in place would render the page against no data and no skeleton.
  const isCaseDataMissing = serviceCaseQuery.isFetching && isNil(serviceCaseQuery.currentData);
  const isLoading = serviceCaseQuery.isLoading || isCaseDataMissing;
  const isError = serviceCaseQuery.isError;

  const operatorCustomer =
    bootstrap?.operatorCustomer ??
    (isNotNil(operatorCustomerId)
      ? {
          customerId: operatorCustomerId,
          displayName: getCustomerName(operatorCustomerFallbackQuery.data),
        }
      : null);

  // The header title needs the bootstrap's operatorCustomer or, on bootstrap failure, the
  // getCustomerV2 fallback chain — keep the skeleton up until whichever source settles.
  const isWaitingForOperatorCustomerFallback =
    isBootstrapSettled &&
    isNil(bootstrap?.operatorCustomer) &&
    (vehicleCustomersFallbackQuery.isLoading ||
      (isNotNil(operatorCustomerId) &&
        isNil(operatorCustomerFallbackQuery.data) &&
        !operatorCustomerFallbackQuery.isError));

  const isHeaderLoading =
    vehicleQuery.isLoading ||
    isBootstrapLoading ||
    isCaseDataMissing ||
    isWaitingForOperatorCustomerFallback;

  const recallMessage =
    bootstrap?.vehicle?.localActionNote ??
    vehicleLocalActionNoteFallbackQuery.currentData?.local_action_note_text;

  const activeServiceCampaignsLength =
    bootstrap?.activeServiceCampaignCount ??
    vehicleServiceCampaignFallbackQuery.data?.serviceCampaign?.filter(
      (campaign) => campaign.kind === 'ACTIVE_CAMPAIGN'
    ).length ??
    0;

  const ordersWithMarginUnderMinimumSnapshot = (
    bootstrap?.ordersWithMarginUnderMinimum ?? []
  ).filter(isNotNil);

  return {
    data: {
      serviceCase: serviceCaseQuery.currentData,
      checkIn: bootstrap?.checkIn ?? checkInFallbackQuery.data,
      vehicle: vehicleQuery.data,
      comments: commentsQuery.data ?? [],
      additionalNote: additionalNoteQuery.data,
      postponedJobs: postponedJobsQuery.data,
      orderVariants: orderVariantsQuery.data,
      vehicleCustomersCount,
      operatorCustomer,
      additionalInformation:
        bootstrap?.additionalInformation ?? additionalInformationFallbackQuery.data,
      serviceCaseOrderCount: serviceCaseOrderCountQuery.data,
      serviceCaseActions: serviceCaseActionsQuery.data,
      recallMessage,
      itemsTotalAmount: itemsTotalAmountQuery.data,
      pendingServiceItems: pendingServiceItemsQuery.data,
      pendingServiceOrderReturn: pendingServiceOrderReturnQuery.data,
      activeServiceCampaignsLength,
      tireSetCount: bootstrap?.tireSetCount ?? tireSetCountFallbackQuery.data?.count,
      boschFleetIntegration: bootstrap?.boschFleetIntegration,
      ordersWithMarginUnderMinimumSnapshot,
    },
    isLoading,
    isError,
    isHeaderLoading,
  };
}
